import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport'
import { computed, getCurrentScope, onScopeDispose, shallowRef } from 'vue'

import { isAbortError } from '@/utils/api'

import type { ComputedRef } from 'vue'

export interface LatestRequestParams<A extends unknown[], T> {
  /**
   * Makes the request, passing `controller` on so the request can be aborted.
   * Receives any arguments given to `run`.
   */
  request: (controller: AbortController, ...args: A) => Promise<T>
  /**
   * Called with the response of the latest request.
   * Never called for a request that has been superseded or aborted.
   */
  onSuccess: (response: T) => void | Promise<void>
  /**
   * Called when the latest request, or its {@link LatestRequestParams.onSuccess onSuccess}, fails.
   * Never called for a request that has been superseded or aborted.
   */
  onError: (error: unknown) => void | Promise<void>
}

export interface LatestRequest<A extends unknown[]> {
  /**
   * Makes a request, aborting the request still in flight from a previous call.
   * Settles once the request and its callbacks have, and never rejects for an aborted request.
   */
  run: (...args: A) => Promise<void>
  /**
   * Aborts the request in flight, if any.
   */
  abort: () => void
  /**
   * Whether a request is in flight.
   * Still `true` when a superseded call settles, until the latest one does.
   */
  readonly pending: boolean
}

/**
 * Creates a {@link LatestRequest} where only the most recent call to `run` is processed.
 *
 * Prefer {@link useLatestRequest} wherever there is a setup context; this is for code without one,
 * such as an options store.
 *
 * @example
 * // within an options store's `state`, where `this` is not yet the store
 * activeRequest: markRaw(createLatestRequest({
 *   request: (controller, req: ListApplicationsRequest) => useApiStore().search.listApplications(req, controller),
 *   onSuccess: (response) => {
 *     useApplicationListStore().applications = response.applications
 *   },
 *   onError: (err) => console.error(err),
 * })),
 *
 * // within an action
 * await this.activeRequest.run(req)
 */
export function createLatestRequest<A extends unknown[], T> (params: LatestRequestParams<A, T>): LatestRequest<A> {
  let activeController: AbortController | null = null

  function abort (): void {
    activeController?.abort(GrpcStatusCode[GrpcStatusCode.CANCELLED])
    activeController = null
  }

  async function run (...args: A): Promise<void> {
    abort()
    const controller = new AbortController()
    activeController = controller
    try {
      const response = await params.request(controller, ...args)
      // the request may ignore the signal, or resolve after being aborted
      if (controller.signal.aborted) return
      await params.onSuccess(response)
    } catch (err) {
      if (controller.signal.aborted || isAbortError(err)) return
      await params.onError(err)
    } finally {
      if (activeController === controller) activeController = null
    }
  }

  return {
    run,
    abort,
    get pending () {
      return activeController !== null
    },
  }
}

export interface UseLatestRequestReturnValue<A extends unknown[]> {
  /**
   * @see {@link LatestRequest.run}
   */
  run: LatestRequest<A>['run']
  /**
   * @see {@link LatestRequest.abort}
   */
  abort: LatestRequest<A>['abort']
  /**
   * Whether a request is in flight.
   * Stays `true` while a superseded request settles, until the latest one does.
   */
  loading: ComputedRef<boolean>
}

/**
 * Reactive wrapper around {@link createLatestRequest}, where only the most recent call to `run` is processed.
 *
 * The request in flight is aborted when the current scope (component, setup store, effect scope) is disposed.
 *
 * @example
 * const { run: searchTasks, loading } = useLatestRequest({
 *   request: (controller) => apiStore.task.list(query.value, controller),
 *   onSuccess: (response) => {
 *     tasks.value = response.tasks
 *   },
 *   onError: (err) => console.error(err),
 * })
 */
export function useLatestRequest<A extends unknown[], T> (params: LatestRequestParams<A, T>): UseLatestRequestReturnValue<A> {
  const latestRequest = createLatestRequest(params)
  const loading = shallowRef(false)

  const run: UseLatestRequestReturnValue<A>['run'] = async (...args) => {
    loading.value = true
    try {
      await latestRequest.run(...args)
    } finally {
      loading.value = latestRequest.pending
    }
  }

  const abort: UseLatestRequestReturnValue<A>['abort'] = () => {
    latestRequest.abort()
    loading.value = false
  }

  if (getCurrentScope()) onScopeDispose(abort)

  return {
    run,
    abort,
    loading: computed(() => loading.value),
  }
}
